import Check from "@mui/icons-material/Check";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button, Chip, Typography } from "@mui/material";
import { Box, Stack, useTheme } from "@mui/system";
import { GridActionsCellItem, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { SubNavAppBar } from "components/AppBar";
import { ExportDataButton } from "components/ExportDataButton";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { renderCopyableCell } from "components/Typography/copyableCell";
import { VENDOR_COLUMN_MIN_WIDTH, VendorColumnHeader } from "components/VendorColumnHeader";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { VALID_MANAGEMENT_METHOD_CODES } from "encamp-shared/src/waste/validCodes";
import { WasteLinesUnifiedQuery, WasteLinesUnifiedQueryVariables } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { getDateLikeIsoDate } from "util/dates";
import { managementMethodCodeOptionLabel, stateOptionLabel } from "util/filterOptionLabels";
import { formatNumber } from "util/formatNumber";

import { renderEManifestAmountCell } from "../EManifestAmountWithOverride";
import { episodicDeclarationsEnabled } from "../EpisodicEvents/episodicFlags";
import { ManifestForEpisodic } from "../EpisodicEvents/markManifestEpisodic";
import { MarkManifestEpisodicDialog } from "../EpisodicEvents/MarkManifestEpisodicDialog";
import { DiscrepancyReviewDialog } from "../Manifests/DiscrepancyReviewDialog";
import { ManifestDetailDrawer } from "../Manifests/ManifestDetailDrawer";
import { WASTE_LINES_UNIFIED_QUERY } from "./api";
import { buildLineItemClassificationColumn } from "./buildLineItemClassificationColumn";
import { ImportButton } from "./ImportButton";
import { LineEpisodicCell } from "./lineEpisodicBadge";
import { lineItemTagsColumn } from "./lineItemTagsColumn";
import { StaffActionsMenu } from "./StaffActionsMenu";
import { UNRESOLVED_DISCREPANCY_COUNT_FIELD } from "./wasteStreamDiscrepanciesLink";
import { YearAutocompletePicker } from "./YearAutocompletePIcker";

type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

function renderLineItemCell({
  row: { willNotReport, wasteStreamWillNotReport },
  value,
}: GridRenderCellParams<WasteLineUnifiedRow>) {
  // Either the line OR the parent stream being flagged means the line
  // is excluded from reporting (the OR matches what reports do at
  // query time). When only the stream is flagged we annotate it
  // explicitly so users can tell the source from the display.
  let label: string | null = null;
  if (willNotReport === true) {
    label = "Not Reportable";
  } else if (wasteStreamWillNotReport === true) {
    label = "Not Reportable (stream)";
  }
  return (
    <Box>
      <TruncateTypography variant="body2">{value}</TruncateTypography>
      {label && (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  );
}

function rowToManifest(row: WasteLineUnifiedRow): ManifestForEpisodic | null {
  const facilityId = row.facility?.id;
  const shippedDate = getDateLikeIsoDate(String(row.shippedDate ?? ""));
  if (!row.processedManifestId || !row.manifestTrackingNumber || !facilityId || !shippedDate) {
    return null;
  }
  return {
    processedManifestId: row.processedManifestId,
    manifestTrackingNumber: row.manifestTrackingNumber,
    facilityId,
    shippedDate,
  };
}

function episodicActionsColumn(
  onMark: (manifest: ManifestForEpisodic) => void
): OmnisearchGridColDef<WasteLineUnifiedRow> {
  return {
    field: "actions",
    type: "actions",
    getActions: ({ row }) => {
      const manifest = rowToManifest(row);
      if (!manifest) {
        return [];
      }
      return [
        <GridActionsCellItem
          key="episodic"
          showInMenu
          label="Mark as episodic"
          onClick={(event) => {
            event.stopPropagation();
            onMark(manifest);
          }}
        />,
      ];
    },
  };
}

export function ManifestLineItems() {
  const theme = useTheme();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteLineUnifiedId");
  const { isStaff } = useCurrentUser();
  const showDiscrepancyCounts = isStaff;
  const { featureFlags } = useFeatureFlags();
  const showEpisodic = episodicDeclarationsEnabled(featureFlags);
  const [marking, setMarking] = useState<ManifestForEpisodic | null>(null);
  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-line-items-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );
  const [selectedRows, setSelectedRows] = useState<WasteLineUnifiedRow[]>([]);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear]
  );

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifest Line Items",
    },
  ]);

  const columns: OmnisearchGridColDef<WasteLineUnifiedRow>[] = useMemo(() => {
    const vendorAmountTooltip =
      "For volumetric conversions, if no density is provided, Encamp uses the density of water as USEPA does in eManifest. If density is provided as a range (e.g. 0.8-1.0), Encamp uses the midpoint when converting to pounds.";

    return [
      {
        field: "lineItem",
        headerName: "Line Item",
        minWidth: 100,
        flex: 2,
        renderCell: renderLineItemCell,
      },
      buildLineItemClassificationColumn<WasteLineUnifiedRow>(),
      {
        field: "vendorAmount",
        headerName: "Amount (Vendor)",
        renderHeader: () => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            Amount (Vendor)
            <InfoIconTooltip tooltip={vendorAmountTooltip} />
          </Stack>
        ),
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.vendorAmount);
          return amount ? `${amount} lbs` : "";
        },
      },
      {
        field: "eManifestAmount",
        headerName: "Amount (e‑Manifest)",
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.eManifestAmount);
          return amount ? `${amount} lbs` : "";
        },
        renderCell: renderEManifestAmountCell,
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        filterKeyType: "date",
        flex: 1,
        valueGetter: (_, row) => {
          return getUtcDisplayDate(row.shippedDate, "med");
        },
      },
      ...(showEpisodic
        ? [
            {
              field: "episodic",
              headerName: "Episodic",
              minWidth: 90,
              flex: 0.7,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: GridRenderCellParams<WasteLineUnifiedRow>) => (
                <LineEpisodicCell exemptions={row.episodicExemptions} />
              ),
            },
          ]
        : []),
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        flex: 1,
        valueGetter: (_, { facility }) => {
          return facility?.name;
        },
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        flex: 1,
        renderCell: renderCopyableCell,
      },
      {
        field: "manifestTrackingNumber",
        headerName: "Document #",
        flex: 1,
      },
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 1,
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        filterKeyType: "enumAutocomplete",
        enumValues: [...VALID_MANAGEMENT_METHOD_CODES],
        enumPresentationFunction: managementMethodCodeOptionLabel,
        flex: 1,
      },
      {
        field: "vendorFinalManagementMethodCode",
        headerName: "Final Management Method Code",
        filterKeyType: "enumAutocomplete",
        enumValues: [...VALID_MANAGEMENT_METHOD_CODES],
        enumPresentationFunction: managementMethodCodeOptionLabel,
        flex: 1,
      },
      {
        field: "federalWasteCodes",
        filterKeyType: "federalWasteCode",
        headerName: "Federal Waste Codes",
        flex: 1,
        valueGetter: (_, { federalWasteCodes }) => {
          return federalWasteCodes?.join(", ");
        },
      },
      {
        field: "stateWasteCodes",
        filterKeyType: "stateWasteCode",
        headerName: "State Waste Codes",
        flex: 1,
        valueGetter: (_, { stateWasteCodes }) => {
          return stateWasteCodes?.join(", ");
        },
      },
      { field: "wasteStream", headerName: "Waste Stream", flex: 1 },
      {
        field: "vendor",
        headerName: "Vendor",
        renderHeader: () => <VendorColumnHeader />,
        flex: 1,
        minWidth: VENDOR_COLUMN_MIN_WIDTH,
        filterKeyType: "wasteVendor",
      },
      ...(showDiscrepancyCounts
        ? [
            {
              field: UNRESOLVED_DISCREPANCY_COUNT_FIELD,
              headerName: "Discrepancies Pending Review",
              flex: 1,
              // Numeric filter (e.g. ">=1" = has ≥1 unresolved). Also what the
              // WasteStreamDetails "Review" deep link targets.
              filterKeyType: "number" as const,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Discrepancies Pending Review
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? (
                  <Chip label={value} size="small" color="warning" variant="outlined" />
                ) : null,
            },
            {
              field: "pendingDiscrepancyCount" as const,
              headerName: "Pending Discrepancies",
              flex: 1,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Pending Discrepancies
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? <Chip label={value} size="small" color="info" variant="outlined" /> : null,
            },
          ]
        : []),
      {
        field: "isEManifest",
        headerName: "e‑Manifest",
        sortable: false,
        flex: 1,
        renderCell: ({ value }) => {
          if (value) {
            return <Check />;
          } else {
            return null;
          }
        },
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        renderCell: ({ value }) => {
          if (value === true) {
            return <Check />;
          }
          return null;
        },
      },
      lineItemTagsColumn<WasteLineUnifiedRow>(),
      ...(showEpisodic ? [episodicActionsColumn(setMarking)] : []),
    ];
  }, [showDiscrepancyCounts, showEpisodic]);

  return (
    <Box>
      <SubNavAppBar
        sx={{
          py: 2,
          px: 3,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
          <Box sx={{ width: 80 }}>
            <YearAutocompletePicker
              label="Year"
              minYear={2018}
              value={shippedDateYear}
              onChange={setShippedDateYear}
            />
          </Box>
        </Stack>
      </SubNavAppBar>
      <Box sx={{ py: 3 }}>
        <OmnisearchDataGrid<
          WasteLinesUnifiedQuery,
          WasteLinesUnifiedQueryVariables,
          WasteLineUnifiedRow
        >
          isURLDriven
          persistenceKey="manifest-line-items"
          initialColumnVisibilityModel={{ episodic: false }}
          columns={columns}
          dataQuery={WASTE_LINES_UNIFIED_QUERY}
          defaultSearch={defaultSearch}
          getItems={(data) => data.wasteLinesUnified?.items ?? []}
          getCount={(data) => data.wasteLinesUnified?.count ?? 0}
          noDataMessage="No manifest line items found."
          initialState={{
            sorting: { sortModel: [{ field: "shippedDate", sort: "desc" }] },
          }}
          rowHeight={"auto"}
          checkboxSelection={isStaff}
          disableRowSelectionOnClick
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={(rows) => setSelectedRows(rows)}
          onRowClick={({ row: { id } }) => pushIdToUrl(id)}
          showFilters
          showManageColumns
          // These staff-only columns auto-generated free-text filters the backend
          // never parses (silent no-ops); suppressed per ENG-15798.
          // unresolvedDiscrepancyCount is now a real number filter (ENG-15798); only
          // pendingDiscrepancyCount stays suppressed (nothing consumes it).
          excludeFilterColumns={["pendingDiscrepancyCount"]}
          additionalFilterColumns={[
            {
              header: "State/Territory",
              key: "states",
              filterKeyType: "enumAutocomplete",
              enumValues: Object.keys(STATE_NAMES),
              enumPresentationFunction: stateOptionLabel,
            },
            {
              header: "Has Vendor Data",
              key: "isVendor",
              filterKeyType: "boolean",
            },
            ...(showEpisodic
              ? [
                  {
                    header: "Has Episodic Event",
                    key: "hasEpisodic",
                    filterKeyType: "boolean" as const,
                  },
                ]
              : []),
            {
              header: "e‑Manifest",
              key: "isEManifest",
              filterKeyType: "boolean",
            },
          ]}
          columnVisibilityModel={{
            federalWasteCodes: false,
            stateWasteCodes: false,
            wasteStream: false,
            willNotReport: false,
          }}
          commandButtons={({ search }) => [
            <ExportDataButton
              key="export"
              fileName={filename}
              tenantId={tenantId}
              filter={{ search }}
              exportType="wasteManifestLines"
            />,
            ...(showDiscrepancyCounts && selectedRows.length > 0
              ? [
                  <Button
                    key="review-discrepancies"
                    variant="outlined"
                    color="staff"
                    size="small"
                    startIcon={<CompareArrowsIcon />}
                    onClick={() => setShowDiscrepancyDialog(true)}
                  >
                    Review Discrepancies ({selectedRows.length})
                  </Button>,
                ]
              : []),
            ...(isStaff ? [<ImportButton key="import" />] : []),
            ...(isStaff
              ? [
                  <StaffActionsMenu
                    key="staff"
                    selectedRows={selectedRows}
                    onCompleted={() => {
                      // Clear selected rows after actions are completed
                      setSelectedRows([]);
                    }}
                  />,
                ]
              : []),
          ]}
        />
      </Box>
      <ManifestDetailDrawer lineItemId={selectedId} open={!!selectedId} onClose={popIdFromUrl} />
      {marking && (
        <MarkManifestEpisodicDialog manifest={marking} open onClose={() => setMarking(null)} />
      )}
      {showDiscrepancyDialog && selectedRows.length > 0 && (
        <DiscrepancyReviewDialog
          processedManifestIds={[...new Set(selectedRows.map((r) => r.processedManifestId))]}
          wasteLineUnifiedIds={selectedRows.map((r) => r.id)}
          onClose={() => setShowDiscrepancyDialog(false)}
        />
      )}
    </Box>
  );
}
